import React, { useState, useRef } from 'react';
import { Calculator as CalcIcon, AlertTriangle, CheckCircle, TrendingDown, DollarSign, GraduationCap, PiggyBank, CreditCard, Home, BookOpen, ChevronDown, ChevronUp, Briefcase, ThumbsUp, ThumbsDown, Minus, Lightbulb, TrendingUp, Printer, BarChart3, PieChart, Target, MapPin, Calendar, Building2, Clock, Wallet } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { mockColleges, majorCategories, findAlternatives } from '../mock';

// ============================================
// REAL BLS/CENSUS SALARY DATA 2024-2025
// ============================================

// Starting salaries (NACE 2025 + BLS entry-level data)
// Mid-career salaries (BLS May 2024 median)
const realSalaryData = {
  // BUSINESS & FINANCE
  "Accounting": { starting: 58000, midCareer: 79880, growth: 0.04 },
  "Business Administration": { starting: 55000, midCareer: 76850, growth: 0.035 },
  "Business Analytics": { starting: 68000, midCareer: 98230, growth: 0.045 },
  "Economics": { starting: 62000, midCareer: 105630, growth: 0.04 },
  "Entrepreneurship": { starting: 50000, midCareer: 72000, growth: 0.035 },
  "Finance": { starting: 71556, midCareer: 99890, growth: 0.045 },
  "Human Resources": { starting: 52000, midCareer: 67650, growth: 0.03 },
  "International Business": { starting: 55000, midCareer: 78000, growth: 0.035 },
  "Management": { starting: 54000, midCareer: 107360, growth: 0.035 },
  "Marketing": { starting: 52000, midCareer: 74600, growth: 0.035 },
  "Real Estate": { starting: 55000, midCareer: 62190, growth: 0.03 },
  "Supply Chain Management": { starting: 62000, midCareer: 98560, growth: 0.04 },
  
  // STEM - COMPUTER & DATA
  "Computer Science": { starting: 82565, midCareer: 136620, growth: 0.055 },
  "Computer Engineering": { starting: 82565, midCareer: 136620, growth: 0.055 },
  "Software Engineering": { starting: 85000, midCareer: 132270, growth: 0.055 },
  "Data Science": { starting: 90000, midCareer: 108020, growth: 0.055 },
  "Cybersecurity": { starting: 82000, midCareer: 120360, growth: 0.055 },
  "Information Technology": { starting: 65000, midCareer: 98740, growth: 0.045 },
  
  // STEM - ENGINEERING
  "Biomedical Engineering": { starting: 72000, midCareer: 100590, growth: 0.045 },
  "Chemical Engineering": { starting: 75000, midCareer: 112100, growth: 0.04 },
  "Civil Engineering": { starting: 68000, midCareer: 95890, growth: 0.035 },
  "Electrical Engineering": { starting: 75000, midCareer: 109370, growth: 0.04 },
  "Environmental Engineering": { starting: 62000, midCareer: 100090, growth: 0.04 },
  "Mechanical Engineering": { starting: 72000, midCareer: 99510, growth: 0.04 },
  
  // STEM - SCIENCES & MATH
  "Applied Mathematics": { starting: 72000, midCareer: 110860, growth: 0.045 },
  "Mathematics": { starting: 60000, midCareer: 96280, growth: 0.04 },
  "Statistics": { starting: 75000, midCareer: 104110, growth: 0.05 },
  "Physics": { starting: 65000, midCareer: 147450, growth: 0.045 },
  "Chemistry": { starting: 50000, midCareer: 84680, growth: 0.035 },
  "Biology": { starting: 45000, midCareer: 57430, growth: 0.03 },
  "Biochemistry": { starting: 55000, midCareer: 106860, growth: 0.04 },
  "Astronomy": { starting: 65000, midCareer: 128330, growth: 0.04 },
  
  // HEALTH & MEDICINE
  "Nursing": { starting: 77600, midCareer: 93600, growth: 0.035 },
  "Pharmacy": { starting: 125000, midCareer: 136030, growth: 0.02 },
  "Physical Therapy": { starting: 75000, midCareer: 99710, growth: 0.035 },
  "Occupational Therapy": { starting: 72000, midCareer: 96370, growth: 0.035 },
  "Health Administration": { starting: 58000, midCareer: 110680, growth: 0.04 },
  "Health Sciences": { starting: 50000, midCareer: 70000, growth: 0.035 },
  "Public Health": { starting: 52000, midCareer: 78520, growth: 0.04 },
  "Dental Hygiene": { starting: 78000, midCareer: 87530, growth: 0.03 },
  "Radiology": { starting: 65000, midCareer: 76020, growth: 0.03 },
  "Medical Technology": { starting: 55000, midCareer: 60780, growth: 0.03 },
  "Neuroscience": { starting: 55000, midCareer: 99650, growth: 0.04 },
  "Pre-Med": { starting: 55000, midCareer: 239200, growth: 0.05 }, // Assumes MD path
  "Pre-Dental": { starting: 55000, midCareer: 170910, growth: 0.04 },
  "Pre-Veterinary": { starting: 48000, midCareer: 119100, growth: 0.035 },
  "Kinesiology": { starting: 45000, midCareer: 52100, growth: 0.03 },
  "Exercise Science": { starting: 45000, midCareer: 52100, growth: 0.03 },
  "Sports Medicine": { starting: 50000, midCareer: 60000, growth: 0.03 },
  "Nutrition": { starting: 52000, midCareer: 69680, growth: 0.03 },
  
  // LAW & PUBLIC POLICY
  "Criminal Justice": { starting: 45000, midCareer: 74910, growth: 0.03 },
  "Criminology": { starting: 45000, midCareer: 74910, growth: 0.03 },
  "Homeland Security": { starting: 55000, midCareer: 80000, growth: 0.035 },
  "International Relations": { starting: 52000, midCareer: 84750, growth: 0.035 },
  "Legal Studies": { starting: 48000, midCareer: 60000, growth: 0.03 },
  "Paralegal Studies": { starting: 50000, midCareer: 60970, growth: 0.03 },
  "Political Science": { starting: 50000, midCareer: 79050, growth: 0.03 },
  "Pre-Law": { starting: 48000, midCareer: 145760, growth: 0.045 }, // Assumes JD path
  "Public Administration": { starting: 52000, midCareer: 85000, growth: 0.035 },
  "Public Policy": { starting: 55000, midCareer: 85000, growth: 0.035 },
  
  // ARTS & HUMANITIES
  "Communications": { starting: 48000, midCareer: 70000, growth: 0.03 },
  "English": { starting: 44000, midCareer: 64000, growth: 0.025 },
  "History": { starting: 45000, midCareer: 63940, growth: 0.025 },
  "Philosophy": { starting: 48000, midCareer: 81000, growth: 0.03 },
  "Psychology": { starting: 42000, midCareer: 90130, growth: 0.035 },
  "Sociology": { starting: 45000, midCareer: 61000, growth: 0.025 },
  "Journalism": { starting: 45000, midCareer: 57500, growth: 0.02 },
  "Film Studies": { starting: 45000, midCareer: 82000, growth: 0.03 },
  "Fine Arts": { starting: 40000, midCareer: 53400, growth: 0.02 },
  "Graphic Design": { starting: 48000, midCareer: 60000, growth: 0.025 },
  "Music": { starting: 38000, midCareer: 54000, growth: 0.02 },
  "Theatre": { starting: 38000, midCareer: 56000, growth: 0.02 },
  "Art History": { starting: 40000, midCareer: 56000, growth: 0.02 },
  "Creative Writing": { starting: 42000, midCareer: 63200, growth: 0.025 },
  "Media Studies": { starting: 46000, midCareer: 65000, growth: 0.025 },
  "Linguistics": { starting: 52000, midCareer: 72000, growth: 0.03 },
  "Languages": { starting: 48000, midCareer: 64000, growth: 0.025 },
  "Religious Studies": { starting: 42000, midCareer: 53000, growth: 0.02 },
  "Anthropology": { starting: 42000, midCareer: 55000, growth: 0.025 },
  "Classics": { starting: 42000, midCareer: 60000, growth: 0.025 },
  "Comparative Literature": { starting: 42000, midCareer: 60000, growth: 0.025 },
  "Writing": { starting: 44000, midCareer: 63200, growth: 0.025 },
  
  // OTHER
  "Education": { starting: 42000, midCareer: 67080, growth: 0.025 },
  "Early Childhood Education": { starting: 38000, midCareer: 37130, growth: 0.02 },
  "Special Education": { starting: 45000, midCareer: 65910, growth: 0.025 },
  "Social Work": { starting: 45000, midCareer: 58380, growth: 0.03 },
  "Architecture": { starting: 55000, midCareer: 93310, growth: 0.035 },
  "Interior Design": { starting: 50000, midCareer: 62510, growth: 0.025 },
  "Urban Planning": { starting: 55000, midCareer: 84640, growth: 0.035 },
  "Environmental Science": { starting: 52000, midCareer: 76480, growth: 0.035 },
  "Environmental Studies": { starting: 48000, midCareer: 63000, growth: 0.03 },
  "Marine Biology": { starting: 45000, midCareer: 66350, growth: 0.03 },
  "Agriculture": { starting: 48000, midCareer: 66390, growth: 0.03 },
  "Forestry": { starting: 52000, midCareer: 67490, growth: 0.03 },
  "Hospitality Management": { starting: 48000, midCareer: 67600, growth: 0.03 },
  "Culinary Arts": { starting: 42000, midCareer: 61320, growth: 0.025 },
  "Fashion Design": { starting: 45000, midCareer: 77450, growth: 0.025 },
  "Aviation": { starting: 80000, midCareer: 148900, growth: 0.04 },
  "Sports Management": { starting: 45000, midCareer: 50000, growth: 0.025 },
  "Military Science": { starting: 55000, midCareer: 75000, growth: 0.03 }
};

// Regional cost of living multipliers (BLS & Census data)
const regionalCostMultipliers = {
  "California": 1.38, "New York": 1.42, "Massachusetts": 1.32, "Connecticut": 1.27,
  "New Jersey": 1.26, "Hawaii": 1.48, "Washington": 1.22, "Colorado": 1.17,
  "Oregon": 1.16, "Maryland": 1.17, "Virginia": 1.12, "Illinois": 1.11,
  "Florida": 1.06, "Texas": 0.98, "Georgia": 0.94, "North Carolina": 0.94,
  "Pennsylvania": 1.04, "Ohio": 0.89, "Michigan": 0.91, "Arizona": 1.01,
  "Tennessee": 0.89, "Indiana": 0.87, "Missouri": 0.87, "Wisconsin": 0.91,
  "Minnesota": 0.97, "Alabama": 0.84, "Kentucky": 0.84, "Louisiana": 0.89,
  "Oklahoma": 0.84, "Kansas": 0.87, "Iowa": 0.87, "Arkansas": 0.81,
  "Mississippi": 0.81, "West Virginia": 0.81, "Nebraska": 0.89, "South Dakota": 0.87,
  "North Dakota": 0.91, "Montana": 0.94, "Wyoming": 0.94, "Idaho": 0.94,
  "Utah": 0.99, "Nevada": 1.06, "New Mexico": 0.91, "Maine": 0.99,
  "Vermont": 1.06, "New Hampshire": 1.11, "Rhode Island": 1.11, "Delaware": 1.06,
  "South Carolina": 0.91, "Alaska": 1.28
};

// Regional salary multipliers (wages tend to be higher in high-cost areas)
const regionalSalaryMultipliers = {
  "California": 1.18, "New York": 1.15, "Massachusetts": 1.14, "Connecticut": 1.10,
  "New Jersey": 1.10, "Hawaii": 1.05, "Washington": 1.15, "Colorado": 1.06,
  "Oregon": 1.02, "Maryland": 1.08, "Virginia": 1.06, "Illinois": 1.04,
  "Florida": 0.96, "Texas": 1.02, "Georgia": 0.98, "North Carolina": 0.96,
  "Pennsylvania": 0.98, "Ohio": 0.92, "Michigan": 0.94, "Arizona": 0.96,
  "Tennessee": 0.94, "Indiana": 0.92, "Missouri": 0.92, "Wisconsin": 0.94,
  "Minnesota": 1.00, "Alabama": 0.88, "Kentucky": 0.88, "Louisiana": 0.90,
  "Oklahoma": 0.88, "Kansas": 0.90, "Iowa": 0.90, "Arkansas": 0.86,
  "Mississippi": 0.84, "West Virginia": 0.86, "Nebraska": 0.92, "South Dakota": 0.88,
  "North Dakota": 0.96, "Montana": 0.88, "Wyoming": 0.92, "Idaho": 0.90,
  "Utah": 0.96, "Nevada": 0.98, "New Mexico": 0.88, "Maine": 0.92,
  "Vermont": 0.94, "New Hampshire": 1.02, "Rhode Island": 0.98, "Delaware": 1.00,
  "South Carolina": 0.90, "Alaska": 1.08
};

// US States list
const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

// ============================================
// HELPER FUNCTIONS
// ============================================

const getSalaryData = (major, state) => {
  const baseData = realSalaryData[major] || { starting: 50000, midCareer: 70000, growth: 0.03 };
  const salaryMult = regionalSalaryMultipliers[state] || 1.0;
  
  return {
    starting: Math.round(baseData.starting * salaryMult),
    midCareer: Math.round(baseData.midCareer * salaryMult),
    growth: baseData.growth
  };
};

const getMajorCategory = (specificMajor) => {
  for (const [category, majors] of Object.entries(majorCategories)) {
    if (majors.includes(specificMajor)) return category;
  }
  return "Other";
};

// Calculate Expected Family Contribution (simplified SAI methodology)
const calculateEFC = (income, assets, familySize, numInCollege) => {
  const incomeProtection = 19500 + (familySize * 4850);
  const availableIncome = Math.max(0, income - incomeProtection);
  const incomeContribution = availableIncome * 0.22;
  const assetContribution = Math.max(0, assets - 10000) * 0.056;
  const totalContribution = (incomeContribution + assetContribution) / numInCollege;
  return Math.max(0, Math.round(totalContribution));
};

// Calculate lifetime earnings and ROI
const calculateROI = (totalCost, startingSalary, midCareerSalary, growthRate, yearsWorking = 40) => {
  let totalEarnings = 0;
  let currentSalary = startingSalary;
  const targetMidCareer = midCareerSalary;
  
  // Salary growth curve - faster early, slower later
  for (let i = 0; i < yearsWorking; i++) {
    totalEarnings += currentSalary;
    if (i < 10) {
      // First 10 years: grow toward mid-career salary
      const progress = (i + 1) / 10;
      currentSalary = startingSalary + (targetMidCareer - startingSalary) * progress;
    } else {
      // After 10 years: slower growth
      currentSalary *= (1 + growthRate * 0.5);
    }
  }
  
  // Compare to high school grad earnings ($35k avg, 1.5% growth)
  let hsEarnings = 0;
  let hsSalary = 35000;
  for (let i = 0; i < yearsWorking + 4; i++) {
    hsEarnings += hsSalary;
    hsSalary *= 1.015;
  }
  
  const earningsPremium = totalEarnings - hsEarnings;
  const netROI = earningsPremium - totalCost;
  const roiPercent = totalCost > 0 ? ((earningsPremium / totalCost) * 100).toFixed(0) : 0;
  
  return { 
    totalEarnings: Math.round(totalEarnings), 
    earningsPremium: Math.round(earningsPremium), 
    netROI: Math.round(netROI), 
    roiPercent,
    hsEarnings: Math.round(hsEarnings)
  };
};

// ============================================
// COMPONENTS
// ============================================

const CollapsibleSection = ({ title, icon: Icon, iconColor, isOpen, onToggle, children, optional = false }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
    <button type="button" onClick={onToggle} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${iconColor}`} />
        <span className="text-base font-semibold text-gray-800">{title}</span>
        {optional && <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">Optional</span>}
      </div>
      {isOpen ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
    </button>
    {isOpen && <div className="p-6 bg-white">{children}</div>}
  </div>
);

// Progress Indicator
const ProgressIndicator = ({ currentStep, totalSteps }) => (
  <div className="flex items-center gap-2 mb-6">
    {Array.from({ length: totalSteps }).map((_, i) => (
      <div key={i} className={`h-2 flex-1 rounded-full transition-all ${i < currentStep ? 'bg-blue-600' : 'bg-gray-200'}`} />
    ))}
    <span className="text-sm text-gray-500 ml-2">{currentStep}/{totalSteps}</span>
  </div>
);

// Simple Pie Chart
const SimplePieChart = ({ data, size = 200 }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = 0;
  
  const createArc = (startAngle, endAngle) => {
    const start = (startAngle - 90) * Math.PI / 180;
    const end = (endAngle - 90) * Math.PI / 180;
    const radius = size / 2 - 10;
    const cx = size / 2;
    const cy = size / 2;
    const x1 = cx + radius * Math.cos(start);
    const y1 = cy + radius * Math.sin(start);
    const x2 = cx + radius * Math.cos(end);
    const y2 = cy + radius * Math.sin(end);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };
  
  return (
    <svg width={size} height={size} className="mx-auto">
      {data.map((item, i) => {
        const angle = (item.value / total) * 360;
        const path = createArc(currentAngle, currentAngle + angle);
        currentAngle += angle;
        return <path key={i} d={path} fill={item.color} className="hover:opacity-80 transition-opacity" />;
      })}
      <circle cx={size/2} cy={size/2} r={size/4} fill="white" />
    </svg>
  );
};

// Bar Chart
const SimpleBarChart = ({ data, maxValue }) => (
  <div className="space-y-3">
    {data.map((item, i) => (
      <div key={i} className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">{item.label}</span>
          <span className="text-gray-900 font-medium">${item.value.toLocaleString()}</span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${(item.value / maxValue) * 100}%`, backgroundColor: item.color }}
          />
        </div>
      </div>
    ))}
  </div>
);

// Debt Payoff Simulator
const DebtPayoffSimulator = ({ totalDebt, basePayment, interestRate }) => {
  const [extraPayment, setExtraPayment] = useState(0);
  
  const calculatePayoff = (extra) => {
    const monthlyRate = interestRate / 12;
    const payment = basePayment + extra;
    let balance = totalDebt;
    let months = 0;
    let totalInterest = 0;
    
    while (balance > 0 && months < 360) {
      const interest = balance * monthlyRate;
      totalInterest += interest;
      balance = balance + interest - payment;
      months++;
    }
    
    return { months, totalInterest: Math.round(totalInterest) };
  };
  
  const baseResult = calculatePayoff(0);
  const extraResult = calculatePayoff(extraPayment);
  const savings = baseResult.totalInterest - extraResult.totalInterest;
  const monthsSaved = baseResult.months - extraResult.months;
  
  return (
    <div className="p-5 bg-gray-50 rounded-xl">
      <h4 className="text-gray-800 font-semibold mb-4 flex items-center gap-2">
        <Target className="w-5 h-5 text-blue-600" />
        See How Extra Payments Help
      </h4>
      <div className="mb-4">
        <Label className="text-gray-600 text-sm mb-2 block">
          Extra Monthly Payment: <span className="font-semibold text-gray-900">${extraPayment}</span>
        </Label>
        <input
          type="range"
          min="0"
          max="500"
          step="25"
          value={extraPayment}
          onChange={(e) => setExtraPayment(Number(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>$0</span>
          <span>$250</span>
          <span>$500</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500 text-xs">Payoff Time</p>
          <p className="text-2xl font-bold text-gray-900">{Math.floor(extraResult.months / 12)}y {extraResult.months % 12}m</p>
          {monthsSaved > 0 && <p className="text-green-600 text-xs font-medium">Save {Math.floor(monthsSaved / 12)}y {monthsSaved % 12}m</p>}
        </div>
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500 text-xs">Interest Saved</p>
          <p className="text-2xl font-bold text-green-600">${savings.toLocaleString()}</p>
          <p className="text-gray-400 text-xs">vs. minimum payments</p>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN CALCULATOR COMPONENT
// ============================================

const Calculator = () => {
  const resultsRef = useRef(null);
  const [formData, setFormData] = useState({
    college: '', familyIncome: '', assets: '0', familySize: '4', numInCollege: '1', homeState: '',
    scholarships: '', grants: '', federalLoans: '', privateLoans: '', workStudy: '',
    savings529: '', otherSavings: '', yearsToStart: '0',
    booksSupplies: '1200', transportation: '1500', personalExpenses: '2000', healthInsurance: '',
    major: '', gpa: '', satScore: ''
  });

  const [collegeSearch, setCollegeSearch] = useState('');
  const [showCollegeDropdown, setShowCollegeDropdown] = useState(false);
  const [results, setResults] = useState(null);
  const [alternatives, setAlternatives] = useState([]);
  const [activeTab, setActiveTab] = useState('summary');
  
  const [showAcademicProfile, setShowAcademicProfile] = useState(false);
  const [showFinancialAid, setShowFinancialAid] = useState(false);
  const [showLoans, setShowLoans] = useState(false);
  const [showSavings, setShowSavings] = useState(false);
  const [showAdditionalCosts, setShowAdditionalCosts] = useState(false);

  const getProgress = () => {
    let filled = 0;
    if (formData.college) filled++;
    if (formData.familyIncome) filled++;
    if (formData.homeState) filled++;
    if (formData.major) filled++;
    return filled;
  };

  const filteredColleges = Object.keys(mockColleges).filter(college =>
    college.toLowerCase().includes(collegeSearch.toLowerCase())
  ).slice(0, 10);

  const handleCollegeSelect = (college) => {
    setFormData({...formData, college: college});
    setCollegeSearch(college);
    setShowCollegeDropdown(false);
  };

  const estimatePellGrant = (income, numInCollege) => {
    const adjustedIncome = income / numInCollege;
    if (adjustedIncome <= 30000) return 7395;
    if (adjustedIncome <= 40000) return 5500;
    if (adjustedIncome <= 50000) return 3500;
    if (adjustedIncome <= 60000) return 1500;
    return 0;
  };

  const estimateMeritAid = (college, gpa, sat) => {
    const collegeData = mockColleges[college];
    if (!collegeData || !gpa) return 0;
    const acceptanceRate = collegeData.a;
    
    if (acceptanceRate > 50) {
      if (gpa >= 3.8 && sat >= 1400) return 18000;
      if (gpa >= 3.5 && sat >= 1300) return 12000;
      if (gpa >= 3.2 && sat >= 1200) return 8000;
      if (gpa >= 3.0) return 4000;
    } else if (acceptanceRate > 30) {
      if (gpa >= 3.9 && sat >= 1500) return 12000;
      if (gpa >= 3.7 && sat >= 1400) return 8000;
      if (gpa >= 3.5 && sat >= 1300) return 4000;
    }
    return 0;
  };

  const estimateWorkStudy = (income) => {
    if (income <= 40000) return 3000;
    if (income <= 60000) return 2500;
    if (income <= 80000) return 2000;
    return 0;
  };

  const handlePrint = () => window.print();

  const handleCalculate = (e) => {
    e.preventDefault();
    const collegeData = mockColleges[formData.college];
    if (!collegeData) return;

    const income = Number(formData.familyIncome) || 0;
    const assets = Number(formData.assets) || 0;
    const familySize = Number(formData.familySize) || 4;
    const numInCollege = Number(formData.numInCollege) || 1;
    const gpa = Number(formData.gpa) || 0;
    const sat = Number(formData.satScore) || 0;
    const yearsToStart = Number(formData.yearsToStart) || 0;

    // In-state tuition check
    const isInState = formData.homeState && collegeData.state === formData.homeState;
    const collegeState = collegeData.state || formData.homeState || "Unknown";
    const costMult = regionalCostMultipliers[collegeState] || 1.0;
    
    // Costs with inflation
    const inflationRate = 0.04;
    const baseTuition = (isInState && collegeData.tIn) ? collegeData.tIn : collegeData.t;
    const tuition = Math.round(baseTuition * Math.pow(1 + inflationRate, yearsToStart));
    const housing = Math.round(collegeData.h * Math.pow(1 + inflationRate, yearsToStart));
    const meals = Math.round(collegeData.m * Math.pow(1 + inflationRate, yearsToStart));
    const fees = Math.round(collegeData.f * Math.pow(1 + inflationRate, yearsToStart));
    
    const booksSupplies = Number(formData.booksSupplies) || 1200;
    const transportation = Math.round((Number(formData.transportation) || 1500) * costMult);
    const personalExpenses = Math.round((Number(formData.personalExpenses) || 2000) * costMult);
    const healthInsurance = Number(formData.healthInsurance) || 0;
    
    const totalCOA = tuition + housing + meals + fees + booksSupplies + transportation + personalExpenses + healthInsurance;
    
    const tuitionType = isInState ? "In-State" : (collegeData.tIn ? "Out-of-State" : "Standard");
    const tuitionSavings = isInState && collegeData.tIn ? (collegeData.t - collegeData.tIn) : 0;

    // EFC
    const efc = calculateEFC(income, assets, familySize, numInCollege);

    // Aid
    const scholarships = Number(formData.scholarships) || 0;
    const grants = Number(formData.grants) || 0;
    const estimatedPell = estimatePellGrant(income, numInCollege);
    const estimatedMerit = estimateMeritAid(formData.college, gpa, sat);
    const workStudyAmount = Number(formData.workStudy) || estimateWorkStudy(income);
    const totalGiftAid = scholarships + grants + estimatedPell + estimatedMerit;

    // Loans
    const federalLoans = Number(formData.federalLoans) || 5500;
    const privateLoans = Number(formData.privateLoans) || 0;
    const totalLoans = federalLoans + privateLoans;

    // Savings
    const totalSavings = (Number(formData.savings529) || 0) + (Number(formData.otherSavings) || 0);
    const savingsPerYear = totalSavings / 4;

    // Net cost
    const annualNetCost = Math.max(0, totalCOA - totalGiftAid - workStudyAmount - savingsPerYear);
    
    // 4-year with inflation
    let fourYearNetCost = 0;
    const yearlyBreakdown = [];
    for (let i = 0; i < 4; i++) {
      const yearCost = Math.round(annualNetCost * Math.pow(1 + inflationRate, i));
      yearlyBreakdown.push(yearCost);
      fourYearNetCost += yearCost;
    }
    const fourYearTotalLoans = totalLoans * 4;

    // Loan repayment
    const interestRate = 0.068;
    const monthlyRate = interestRate / 12;
    const numPayments = 120;
    const monthlyPayment = fourYearTotalLoans > 0 
      ? (fourYearTotalLoans * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1)
      : 0;
    const totalRepayment = monthlyPayment * numPayments;
    const totalInterest = totalRepayment - fourYearTotalLoans;

    // Career projections with real BLS data
    const salaryData = getSalaryData(formData.major, formData.homeState);
    const majorCategory = getMajorCategory(formData.major);
    
    // Take-home calculation
    const federalTax = salaryData.starting > 95375 ? 0.24 : salaryData.starting > 44725 ? 0.22 : salaryData.starting > 11600 ? 0.12 : 0.10;
    const stateTaxRates = { "California": 0.093, "New York": 0.085, "New Jersey": 0.064, "Oregon": 0.099, "Minnesota": 0.0785, "Texas": 0, "Florida": 0, "Washington": 0, "Nevada": 0 };
    const stateTax = stateTaxRates[formData.homeState] || 0.05;
    const fica = 0.0765;
    const monthlyGross = salaryData.starting / 12;
    const monthlyTaxes = monthlyGross * (federalTax + stateTax + fica);
    const monthlyTakeHome = Math.round(monthlyGross - monthlyTaxes);
    
    // Living expenses by region
    const baseRent = { "California": 2200, "New York": 2400, "Massachusetts": 2000, "Washington": 1800, "Colorado": 1700, "Texas": 1400, "Florida": 1500, "Ohio": 1100, "Michigan": 1200 };
    const monthlyRent = Math.round((baseRent[formData.homeState] || 1400) * 1.0);
    const monthlyUtilities = 180;
    const monthlyFood = Math.round(450 * costMult);
    const monthlyTransport = 400;
    const monthlyPhone = 80;
    const monthlyOther = 250;
    const monthlyExpenses = monthlyRent + monthlyUtilities + monthlyFood + monthlyTransport + monthlyPhone + monthlyOther;
    const monthlyLeftover = monthlyTakeHome - Math.round(monthlyPayment) - monthlyExpenses;

    // ROI
    const roi = calculateROI(fourYearNetCost + fourYearTotalLoans, salaryData.starting, salaryData.midCareer, salaryData.growth);

    // Savings goal
    const monthsUntilCollege = yearsToStart * 12 || 1;
    const monthlySavingsNeeded = Math.round((fourYearNetCost - totalSavings) / (monthsUntilCollege + 48));
    
    // Time to payoff in years
    const yearsToPayoff = Math.ceil(numPayments / 12);
    const payoffAge = 22 + yearsToPayoff;
    
    // Verdicts
    const costToIncomeRatio = (annualNetCost / income) * 100;
    const debtToFirstYearRatio = fourYearTotalLoans / salaryData.starting * 100;
    
    let affordabilityVerdict, debtVerdict, lifestyleVerdict;
    
    if (costToIncomeRatio <= 15) {
      affordabilityVerdict = { rating: "good", text: "Very affordable for your family", detail: `At ${costToIncomeRatio.toFixed(0)}% of family income, this is well within the recommended 15-20% range.` };
    } else if (costToIncomeRatio <= 25) {
      affordabilityVerdict = { rating: "okay", text: "Manageable with budgeting", detail: `At ${costToIncomeRatio.toFixed(0)}% of income, it's above ideal but doable with planning.` };
    } else {
      affordabilityVerdict = { rating: "bad", text: "May stretch your budget", detail: `At ${costToIncomeRatio.toFixed(0)}% of income, this exceeds recommended limits. Consider alternatives.` };
    }

    if (debtToFirstYearRatio <= 50) {
      debtVerdict = { rating: "good", text: "Manageable debt level", detail: `Total debt will be ${debtToFirstYearRatio.toFixed(0)}% of your expected first-year salary—below the recommended limit.` };
    } else if (debtToFirstYearRatio <= 100) {
      debtVerdict = { rating: "okay", text: "Moderate debt level", detail: `Debt equals ${debtToFirstYearRatio.toFixed(0)}% of first-year salary. Noticeable but manageable.` };
    } else {
      debtVerdict = { rating: "bad", text: "High debt compared to earnings", detail: `Debt will be ${debtToFirstYearRatio.toFixed(0)}% of first-year salary—above recommended limits.` };
    }

    if (monthlyLeftover >= 800) {
      lifestyleVerdict = { rating: "good", text: "Comfortable post-grad budget", detail: `About $${monthlyLeftover.toLocaleString()}/month for savings, emergencies, and fun.` };
    } else if (monthlyLeftover >= 300) {
      lifestyleVerdict = { rating: "okay", text: "Tight but workable", detail: `About $${monthlyLeftover.toLocaleString()}/month after necessities. Build an emergency fund first.` };
    } else {
      lifestyleVerdict = { rating: "bad", text: "Budget will be very tight", detail: `Only $${Math.max(0, monthlyLeftover).toLocaleString()}/month left. Consider living with family initially.` };
    }

    // Smart tips
    const tips = [];
    if (costToIncomeRatio > 25) tips.push("Apply for 10+ scholarships—even small ones add up to real savings");
    if (debtToFirstYearRatio > 75) tips.push("Consider community college for 2 years to save $20,000+");
    if (monthlyLeftover < 500) tips.push("Plan to live with roommates or family for the first few years after graduation");
    if (estimatedMerit === 0 && gpa >= 3.5) tips.push("Your GPA may qualify you for merit scholarships—ask the financial aid office");
    if (privateLoans > 0) tips.push("Max out federal loans before private—better interest rates and repayment options");
    if (totalSavings === 0) tips.push("Starting a 529 plan now, even with $50/month, helps reduce future borrowing");
    if (tuitionSavings > 0) tips.push(`In-state tuition saves you $${tuitionSavings.toLocaleString()}/year—$${(tuitionSavings * 4).toLocaleString()} over 4 years!`);

    // Chart data
    const costBreakdownData = [
      { label: 'Tuition & Fees', value: tuition + fees, color: '#2563eb' },
      { label: 'Housing', value: housing, color: '#0891b2' },
      { label: 'Meals', value: meals, color: '#7c3aed' },
      { label: 'Books & Supplies', value: booksSupplies, color: '#ea580c' },
      { label: 'Transportation', value: transportation, color: '#dc2626' },
      { label: 'Personal & Other', value: personalExpenses + healthInsurance, color: '#db2777' },
    ];

    setResults({
      annualNetCost, fourYearNetCost, totalCOA, totalGiftAid, fourYearTotalLoans,
      monthlyPayment: Math.round(monthlyPayment),
      totalInterest: Math.round(totalInterest),
      totalRepayment: Math.round(totalRepayment),
      efc, isInState, tuitionType, tuitionSavings, collegeState, costMult,
      yearlyBreakdown,
      major: formData.major, majorCategory,
      salaryData,
      monthlyTakeHome, monthlyExpenses, monthlyLeftover, monthlyRent,
      roi, monthlySavingsNeeded, yearsToPayoff, payoffAge,
      affordabilityVerdict, debtVerdict, lifestyleVerdict, tips,
      breakdown: { tuition, housing, meals, fees, booksSupplies, transportation, personalExpenses },
      aidBreakdown: { scholarships, grants, estimatedPell, estimatedMerit, workStudy: workStudyAmount },
      totalSavings,
      costBreakdownData,
      interestRate
    });

    const alts = findAlternatives(formData.college, formData.major, "always");
    setAlternatives(alts);
    
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white">
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10 print:mb-6">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4 print:hidden">
              <CalcIcon className="w-7 h-7 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 print:text-2xl">College Cost Calculator</h1>
            <p className="text-gray-600 max-w-xl mx-auto">
              Calculate your true college costs with real salary data from the Bureau of Labor Statistics
            </p>
          </div>

          {/* Form */}
          <Card className="bg-white border-gray-200 shadow-sm p-6 mb-8 print:hidden">
            <ProgressIndicator currentStep={getProgress()} totalSteps={4} />
            
            <form onSubmit={handleCalculate} className="space-y-6">
              {/* Required Fields */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-blue-600" />
                  Basic Information
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2 relative">
                    <Label className="text-gray-700 text-sm">College</Label>
                    <Input
                      placeholder="Type to search colleges..."
                      value={collegeSearch}
                      onChange={(e) => {
                        setCollegeSearch(e.target.value);
                        setShowCollegeDropdown(true);
                        if (e.target.value !== formData.college) setFormData({...formData, college: ''});
                      }}
                      onFocus={() => setShowCollegeDropdown(true)}
                      className="bg-white border-gray-300 text-gray-900 h-11"
                      required
                    />
                    {showCollegeDropdown && collegeSearch && filteredColleges.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                        {filteredColleges.map((college) => (
                          <div key={college} className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-gray-900 text-sm" onClick={() => handleCollegeSelect(college)}>
                            {college}
                            {mockColleges[college]?.state && <span className="text-gray-500 ml-2">({mockColleges[college].state})</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-700 text-sm">Family Income (per year)</Label>
                    <Input type="number" placeholder="75000" value={formData.familyIncome} onChange={(e) => setFormData({...formData, familyIncome: e.target.value})} className="bg-white border-gray-300 text-gray-900 h-11" required />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-700 text-sm">Your Home State</Label>
                    <Select value={formData.homeState} onValueChange={(value) => setFormData({...formData, homeState: value})}>
                      <SelectTrigger className="bg-white border-gray-300 text-gray-900 h-11"><SelectValue placeholder="Select state" /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 max-h-[300px]">
                        {US_STATES.map((state) => (
                          <SelectItem key={state} value={state} className="text-gray-900">{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">For in-state tuition & salary estimates</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-700 text-sm">Intended Major</Label>
                    <Select value={formData.major} onValueChange={(value) => setFormData({...formData, major: value})}>
                      <SelectTrigger className="bg-white border-gray-300 text-gray-900 h-11"><SelectValue placeholder="Select your major" /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 max-h-[350px]">
                        {Object.entries(majorCategories).map(([category, majors]) => (
                          <div key={category}>
                            <div className="px-2 py-2 text-xs font-semibold text-blue-600 uppercase bg-gray-50 sticky top-0">{category}</div>
                            {majors.map((major) => (<SelectItem key={major} value={major} className="text-gray-900 pl-4">{major}</SelectItem>))}
                          </div>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-700 text-sm">Kids in College</Label>
                    <Select value={formData.numInCollege} onValueChange={(value) => setFormData({...formData, numInCollege: value})}>
                      <SelectTrigger className="bg-white border-gray-300 text-gray-900 h-11"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="1" className="text-gray-900">1</SelectItem>
                        <SelectItem value="2" className="text-gray-900">2</SelectItem>
                        <SelectItem value="3" className="text-gray-900">3+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-700 text-sm">Family Size</Label>
                    <Select value={formData.familySize} onValueChange={(value) => setFormData({...formData, familySize: value})}>
                      <SelectTrigger className="bg-white border-gray-300 text-gray-900 h-11"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {[2,3,4,5,6,7,8].map(n => <SelectItem key={n} value={String(n)} className="text-gray-900">{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Optional Sections */}
              <div className="space-y-3 pt-4">
                <p className="text-sm text-gray-500">Optional: Add more details for accurate results</p>

                <CollapsibleSection title="Academic Stats" icon={BookOpen} iconColor="text-cyan-600" isOpen={showAcademicProfile} onToggle={() => setShowAcademicProfile(!showAcademicProfile)} optional>
                  <p className="text-sm text-gray-500 mb-4">Helps estimate merit scholarships.</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">GPA</Label>
                      <Input type="number" step="0.01" min="0" max="4.0" placeholder="3.5" value={formData.gpa} onChange={(e) => setFormData({...formData, gpa: e.target.value})} className="bg-white border-gray-300 text-gray-900 h-11" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">SAT Score</Label>
                      <Input type="number" min="400" max="1600" placeholder="1200" value={formData.satScore} onChange={(e) => setFormData({...formData, satScore: e.target.value})} className="bg-white border-gray-300 text-gray-900 h-11" />
                    </div>
                  </div>
                </CollapsibleSection>

                <CollapsibleSection title="Scholarships & Grants" icon={DollarSign} iconColor="text-green-600" isOpen={showFinancialAid} onToggle={() => setShowFinancialAid(!showFinancialAid)} optional>
                  <p className="text-sm text-gray-500 mb-4">Enter any money you've already been awarded (per year).</p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">Scholarships</Label>
                      <Input type="number" placeholder="0" value={formData.scholarships} onChange={(e) => setFormData({...formData, scholarships: e.target.value})} className="bg-white border-gray-300 text-gray-900 h-11" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">Grants</Label>
                      <Input type="number" placeholder="0" value={formData.grants} onChange={(e) => setFormData({...formData, grants: e.target.value})} className="bg-white border-gray-300 text-gray-900 h-11" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">Work-Study</Label>
                      <Input type="number" placeholder="Auto-estimated" value={formData.workStudy} onChange={(e) => setFormData({...formData, workStudy: e.target.value})} className="bg-white border-gray-300 text-gray-900 h-11" />
                    </div>
                  </div>
                </CollapsibleSection>

                <CollapsibleSection title="Planned Loans" icon={CreditCard} iconColor="text-orange-600" isOpen={showLoans} onToggle={() => setShowLoans(!showLoans)} optional>
                  <p className="text-sm text-gray-500 mb-4">Default: $5,500/year federal loans (freshman limit).</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">Federal Loans (per year)</Label>
                      <Input type="number" placeholder="5500" value={formData.federalLoans} onChange={(e) => setFormData({...formData, federalLoans: e.target.value})} className="bg-white border-gray-300 text-gray-900 h-11" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">Private Loans (per year)</Label>
                      <Input type="number" placeholder="0" value={formData.privateLoans} onChange={(e) => setFormData({...formData, privateLoans: e.target.value})} className="bg-white border-gray-300 text-gray-900 h-11" />
                    </div>
                  </div>
                </CollapsibleSection>

                <CollapsibleSection title="College Savings" icon={PiggyBank} iconColor="text-purple-600" isOpen={showSavings} onToggle={() => setShowSavings(!showSavings)} optional>
                  <p className="text-sm text-gray-500 mb-4">Total savings available for all 4 years.</p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">529 Plan</Label>
                      <Input type="number" placeholder="0" value={formData.savings529} onChange={(e) => setFormData({...formData, savings529: e.target.value})} className="bg-white border-gray-300 text-gray-900 h-11" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">Other Savings</Label>
                      <Input type="number" placeholder="0" value={formData.otherSavings} onChange={(e) => setFormData({...formData, otherSavings: e.target.value})} className="bg-white border-gray-300 text-gray-900 h-11" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">Years Until Start</Label>
                      <Select value={formData.yearsToStart} onValueChange={(value) => setFormData({...formData, yearsToStart: value})}>
                        <SelectTrigger className="bg-white border-gray-300 text-gray-900 h-11"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          {[0,1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)} className="text-gray-900">{n === 0 ? 'Starting this year' : `In ${n} year${n > 1 ? 's' : ''}`}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CollapsibleSection>

                <CollapsibleSection title="Additional Costs" icon={Home} iconColor="text-gray-500" isOpen={showAdditionalCosts} onToggle={() => setShowAdditionalCosts(!showAdditionalCosts)} optional>
                  <p className="text-sm text-gray-500 mb-4">Typical estimates included. Adjust if needed.</p>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">Books</Label>
                      <Input type="number" placeholder="1200" value={formData.booksSupplies} onChange={(e) => setFormData({...formData, booksSupplies: e.target.value})} className="bg-white border-gray-300 text-gray-900 h-11" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">Travel</Label>
                      <Input type="number" placeholder="1500" value={formData.transportation} onChange={(e) => setFormData({...formData, transportation: e.target.value})} className="bg-white border-gray-300 text-gray-900 h-11" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">Personal</Label>
                      <Input type="number" placeholder="2000" value={formData.personalExpenses} onChange={(e) => setFormData({...formData, personalExpenses: e.target.value})} className="bg-white border-gray-300 text-gray-900 h-11" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">Health Ins.</Label>
                      <Input type="number" placeholder="0" value={formData.healthInsurance} onChange={(e) => setFormData({...formData, healthInsurance: e.target.value})} className="bg-white border-gray-300 text-gray-900 h-11" />
                    </div>
                  </div>
                </CollapsibleSection>
              </div>

              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold" disabled={!formData.college || !formData.familyIncome || !formData.major}>
                Calculate True Cost
              </Button>
            </form>
          </Card>

          {/* RESULTS */}
          {results && (
            <div ref={resultsRef} className="space-y-6 animate-in fade-in duration-500">
              
              {/* Print Button */}
              <div className="flex justify-end print:hidden">
                <Button onClick={handlePrint} variant="outline" className="border-gray-300 text-gray-600 hover:bg-gray-100">
                  <Printer className="w-4 h-4 mr-2" /> Save as PDF
                </Button>
              </div>

              {/* Tabs */}
              <div className="flex gap-2 overflow-x-auto pb-2 print:hidden">
                {[
                  { id: 'summary', label: 'Summary', icon: CalcIcon },
                  { id: 'breakdown', label: 'Cost Details', icon: PieChart },
                  { id: 'career', label: 'After Graduation', icon: Briefcase },
                  { id: 'payoff', label: 'Loan Payoff', icon: Target },
                ].map(tab => (
                  <Button
                    key={tab.id}
                    variant={activeTab === tab.id ? 'default' : 'ghost'}
                    className={activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <tab.icon className="w-4 h-4 mr-2" /> {tab.label}
                  </Button>
                ))}
              </div>

              {/* Summary Tab */}
              {activeTab === 'summary' && (
                <>
                  {/* Bottom Line */}
                  <Card className="bg-white border-gray-200 p-8 shadow-sm">
                    <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">Your True College Cost</h2>
                    
                    {results.isInState && (
                      <div className="flex justify-center mb-6">
                        <span className="bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          In-State Tuition • Saving ${results.tuitionSavings.toLocaleString()}/year
                        </span>
                      </div>
                    )}
                    
                    <div className="grid md:grid-cols-4 gap-6 text-center">
                      <div className="p-4 bg-blue-50 rounded-xl">
                        <p className="text-gray-600 text-sm mb-1">Annual Net Cost</p>
                        <p className="text-3xl font-bold text-blue-600">${results.annualNetCost.toLocaleString()}</p>
                        <p className="text-gray-500 text-xs">per year after aid</p>
                      </div>
                      <div className="p-4 bg-gray-100 rounded-xl">
                        <p className="text-gray-600 text-sm mb-1">4-Year Total</p>
                        <p className="text-3xl font-bold text-gray-900">${results.fourYearNetCost.toLocaleString()}</p>
                        <p className="text-gray-500 text-xs">with 4% inflation</p>
                      </div>
                      <div className="p-4 bg-orange-50 rounded-xl">
                        <p className="text-gray-600 text-sm mb-1">Monthly Payment</p>
                        <p className="text-3xl font-bold text-orange-600">${results.monthlyPayment}/mo</p>
                        <p className="text-gray-500 text-xs">for {results.yearsToPayoff} years</p>
                      </div>
                      <div className="p-4 bg-cyan-50 rounded-xl">
                        <p className="text-gray-600 text-sm mb-1">Est. Family Contribution</p>
                        <p className="text-3xl font-bold text-cyan-600">${results.efc.toLocaleString()}</p>
                        <p className="text-gray-500 text-xs">expected per year</p>
                      </div>
                    </div>
                  </Card>

                  {/* What This Means */}
                  <Card className="bg-white border-gray-200 p-6 shadow-sm">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">What This Means For You</h2>
                    <div className="space-y-4">
                      {[results.affordabilityVerdict, results.debtVerdict, results.lifestyleVerdict].map((verdict, i) => (
                        <div key={i} className={`p-4 rounded-lg border ${verdict.rating === 'good' ? 'bg-green-50 border-green-200' : verdict.rating === 'okay' ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            {verdict.rating === 'good' ? <ThumbsUp className="w-5 h-5 text-green-600" /> : verdict.rating === 'okay' ? <Minus className="w-5 h-5 text-yellow-600" /> : <ThumbsDown className="w-5 h-5 text-red-600" />}
                            <span className={`font-semibold ${verdict.rating === 'good' ? 'text-green-700' : verdict.rating === 'okay' ? 'text-yellow-700' : 'text-red-700'}`}>{verdict.text}</span>
                          </div>
                          <p className="text-gray-600 text-sm">{verdict.detail}</p>
                        </div>
                      ))}
                    </div>
                  </Card>

                  {/* Quick Career Preview */}
                  <Card className="bg-white border-gray-200 p-6 shadow-sm">
                    <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-blue-600" />
                      Quick Career Preview: {results.major}
                    </h2>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                        <p className="text-gray-600 text-sm">Starting Salary</p>
                        <p className="text-2xl font-bold text-green-600">${results.salaryData.starting.toLocaleString()}</p>
                        <p className="text-gray-500 text-xs">BLS/NACE 2024-25 data</p>
                      </div>
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
                        <p className="text-gray-600 text-sm">Mid-Career Salary</p>
                        <p className="text-2xl font-bold text-blue-600">${results.salaryData.midCareer.toLocaleString()}</p>
                        <p className="text-gray-500 text-xs">~10 years experience</p>
                      </div>
                      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg text-center">
                        <p className="text-gray-600 text-sm">Lifetime ROI</p>
                        <p className="text-2xl font-bold text-purple-600">{results.roi.roiPercent}%</p>
                        <p className="text-gray-500 text-xs">vs. high school diploma</p>
                      </div>
                    </div>
                  </Card>

                  {/* Savings Goal */}
                  {results.monthlySavingsNeeded > 0 && (
                    <Card className="bg-purple-50 border-purple-200 p-6">
                      <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <PiggyBank className="w-5 h-5 text-purple-600" />
                        Your Savings Goal
                      </h2>
                      <div className="grid md:grid-cols-3 gap-4 text-center">
                        <div className="p-4 bg-white rounded-lg">
                          <p className="text-gray-600 text-sm">Save monthly</p>
                          <p className="text-3xl font-bold text-purple-600">${results.monthlySavingsNeeded}</p>
                          <p className="text-gray-500 text-xs">through graduation</p>
                        </div>
                        <div className="p-4 bg-white rounded-lg">
                          <p className="text-gray-600 text-sm">Or weekly</p>
                          <p className="text-3xl font-bold text-purple-600">${Math.round(results.monthlySavingsNeeded / 4)}</p>
                          <p className="text-gray-500 text-xs">more manageable</p>
                        </div>
                        <div className="p-4 bg-white rounded-lg">
                          <p className="text-gray-600 text-sm">Gap to fill</p>
                          <p className="text-3xl font-bold text-gray-900">${(results.fourYearNetCost - results.totalSavings).toLocaleString()}</p>
                          <p className="text-gray-500 text-xs">after current savings</p>
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* Tips */}
                  {results.tips.length > 0 && (
                    <Card className="bg-blue-50 border-blue-200 p-6">
                      <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Lightbulb className="w-5 h-5 text-blue-600" />
                        Tips For Your Situation
                      </h2>
                      <div className="space-y-2">
                        {results.tips.map((tip, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 bg-white rounded-lg">
                            <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <p className="text-gray-700 text-sm">{tip}</p>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}

                  {/* Alternatives */}
                  <Card className="bg-white border-gray-200 p-6 shadow-sm">
                    <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <TrendingDown className="w-5 h-5 text-green-600" />
                      Similar Schools, Lower Prices
                    </h2>
                    {alternatives.length > 0 ? (
                      <div className="space-y-3">
                        {alternatives.map((alt) => (
                          <div key={alt.name} className="flex justify-between items-center bg-gray-50 rounded-lg p-4">
                            <div>
                              <h3 className="text-gray-900 font-semibold">{alt.name}</h3>
                              <p className="text-xs text-gray-500">{alt.acceptanceRate}% acceptance</p>
                            </div>
                            <div className="text-right">
                              <p className="text-gray-900 font-bold">${alt.cost.toLocaleString()}/yr</p>
                              <p className={`text-sm font-medium ${alt.savings > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                                {alt.savings > 0 ? `Save $${alt.savings.toLocaleString()}/yr` : 'Similar price'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 p-4 bg-gray-50 rounded-lg text-center">This is already one of the more affordable options!</p>
                    )}
                  </Card>
                </>
              )}

              {/* Cost Breakdown Tab */}
              {activeTab === 'breakdown' && (
                <Card className="bg-white border-gray-200 p-6 shadow-sm">
                  <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-blue-600" />
                    Where Your Money Goes
                  </h2>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-base font-semibold text-gray-800 mb-4 text-center">Annual Cost Breakdown</h3>
                      <SimplePieChart data={results.costBreakdownData} />
                      <div className="mt-4 grid grid-cols-2 gap-2">
                        {results.costBreakdownData.map((item, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                            <span className="text-gray-600">{item.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-800 mb-4">Cost by Category</h3>
                      <SimpleBarChart data={results.costBreakdownData} maxValue={Math.max(...results.costBreakdownData.map(d => d.value))} />
                    </div>
                  </div>

                  {/* 4-Year Projection */}
                  <div className="mt-8 p-5 bg-gray-50 rounded-xl">
                    <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-orange-600" />
                      4-Year Cost Projection (4% annual inflation)
                    </h3>
                    <div className="grid grid-cols-4 gap-4 text-center">
                      {results.yearlyBreakdown.map((cost, i) => (
                        <div key={i} className="p-4 bg-white rounded-lg border border-gray-200">
                          <p className="text-gray-500 text-xs">Year {i + 1}</p>
                          <p className="text-xl font-bold text-gray-900">${cost.toLocaleString()}</p>
                          {i > 0 && <p className="text-orange-600 text-xs">+${(cost - results.yearlyBreakdown[i-1]).toLocaleString()}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}

              {/* Career Tab - More Detailed */}
              {activeTab === 'career' && (
                <Card className="bg-white border-gray-200 p-6 shadow-sm">
                  <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-blue-600" />
                    Life After Graduation: {results.major}
                  </h2>
                  
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Data Source:</strong> Starting salaries from NACE Class of 2025 projections. 
                      Mid-career salaries from BLS Occupational Employment Statistics (May 2024). 
                      Regional adjustments based on BLS cost-of-living data.
                    </p>
                  </div>

                  {/* Salary Cards */}
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    <div className="p-5 bg-green-50 border border-green-200 rounded-xl text-center">
                      <Building2 className="w-6 h-6 text-green-600 mx-auto mb-2" />
                      <p className="text-gray-600 text-sm">Starting Salary</p>
                      <p className="text-3xl font-bold text-green-600">${results.salaryData.starting.toLocaleString()}</p>
                      <p className="text-gray-500 text-xs mt-1">Entry-level in {formData.homeState || 'your area'}</p>
                    </div>
                    <div className="p-5 bg-blue-50 border border-blue-200 rounded-xl text-center">
                      <TrendingUp className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                      <p className="text-gray-600 text-sm">Mid-Career Salary</p>
                      <p className="text-3xl font-bold text-blue-600">${results.salaryData.midCareer.toLocaleString()}</p>
                      <p className="text-gray-500 text-xs mt-1">~10 years experience</p>
                    </div>
                    <div className="p-5 bg-purple-50 border border-purple-200 rounded-xl text-center">
                      <BarChart3 className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                      <p className="text-gray-600 text-sm">Salary Growth</p>
                      <p className="text-3xl font-bold text-purple-600">{(results.salaryData.growth * 100).toFixed(1)}%</p>
                      <p className="text-gray-500 text-xs mt-1">annual growth rate</p>
                    </div>
                  </div>

                  {/* Monthly Budget */}
                  <div className="p-5 bg-gray-50 rounded-xl mb-6">
                    <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-gray-600" />
                      Your Monthly Budget After Graduation
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                        <span className="text-gray-600">Monthly Take-Home Pay</span>
                        <span className="text-xl font-bold text-green-600">+${results.monthlyTakeHome.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                        <span className="text-gray-600">Rent ({formData.homeState || 'average'})</span>
                        <span className="text-gray-900 font-medium">-${results.monthlyRent.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                        <span className="text-gray-600">Food, utilities, transport, other</span>
                        <span className="text-gray-900 font-medium">-${(results.monthlyExpenses - results.monthlyRent).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                        <span className="text-gray-600">Student Loan Payment</span>
                        <span className="text-orange-600 font-medium">-${results.monthlyPayment.toLocaleString()}</span>
                      </div>
                      <div className={`flex justify-between items-center p-3 rounded-lg ${results.monthlyLeftover >= 300 ? 'bg-green-100' : 'bg-red-100'}`}>
                        <span className="font-semibold text-gray-800">What's Left Each Month</span>
                        <span className={`text-xl font-bold ${results.monthlyLeftover >= 300 ? 'text-green-600' : 'text-red-600'}`}>${results.monthlyLeftover.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* ROI Analysis */}
                  <div className="p-5 bg-purple-50 border border-purple-200 rounded-xl">
                    <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                      Is This Degree Worth It? (Return on Investment)
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="p-4 bg-white rounded-lg text-center">
                        <p className="text-gray-600 text-sm">Your Total Investment</p>
                        <p className="text-2xl font-bold text-gray-900">${(results.fourYearNetCost + results.fourYearTotalLoans).toLocaleString()}</p>
                        <p className="text-gray-500 text-xs">college cost + debt</p>
                      </div>
                      <div className="p-4 bg-white rounded-lg text-center">
                        <p className="text-gray-600 text-sm">Extra Lifetime Earnings</p>
                        <p className="text-2xl font-bold text-purple-600">${(results.roi.earningsPremium / 1000000).toFixed(1)}M</p>
                        <p className="text-gray-500 text-xs">vs. high school diploma</p>
                      </div>
                    </div>
                    <div className="p-4 bg-white rounded-lg">
                      <p className="text-gray-700 text-sm">
                        <strong className="text-purple-600">Return on Investment: {results.roi.roiPercent}%</strong> — 
                        {Number(results.roi.roiPercent) >= 300 
                          ? " Excellent! For every $1 invested, you'll earn back $" + (Number(results.roi.roiPercent) / 100).toFixed(1) + " over your career."
                          : Number(results.roi.roiPercent) >= 150 
                          ? " Good return. This degree should pay for itself and more."
                          : " Consider lower-cost alternatives or higher-earning majors for better returns."}
                      </p>
                    </div>
                  </div>

                  {/* Key Milestones */}
                  <div className="mt-6 p-5 bg-gray-50 rounded-xl">
                    <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-gray-600" />
                      Your Financial Timeline
                    </h3>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex-1 min-w-[150px] p-3 bg-white rounded-lg text-center">
                        <p className="text-gray-500 text-xs">Graduate</p>
                        <p className="text-lg font-bold text-gray-900">Age 22</p>
                      </div>
                      <div className="flex-1 min-w-[150px] p-3 bg-white rounded-lg text-center">
                        <p className="text-gray-500 text-xs">Loans Paid Off</p>
                        <p className="text-lg font-bold text-green-600">Age {results.payoffAge}</p>
                      </div>
                      <div className="flex-1 min-w-[150px] p-3 bg-white rounded-lg text-center">
                        <p className="text-gray-500 text-xs">Mid-Career</p>
                        <p className="text-lg font-bold text-blue-600">Age 32</p>
                      </div>
                      <div className="flex-1 min-w-[150px] p-3 bg-white rounded-lg text-center">
                        <p className="text-gray-500 text-xs">Earnings Premium</p>
                        <p className="text-lg font-bold text-purple-600">${(results.roi.earningsPremium / 1000000).toFixed(1)}M</p>
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Loan Payoff Tab */}
              {activeTab === 'payoff' && (
                <Card className="bg-white border-gray-200 p-6 shadow-sm">
                  <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-600" />
                    Your Loan Repayment Plan
                  </h2>
                  
                  <DebtPayoffSimulator 
                    totalDebt={results.fourYearTotalLoans} 
                    basePayment={results.monthlyPayment}
                    interestRate={results.interestRate}
                  />

                  <div className="mt-6 grid md:grid-cols-2 gap-4">
                    <div className="p-5 bg-gray-50 rounded-xl">
                      <h4 className="text-gray-800 font-semibold mb-3">Standard 10-Year Plan</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-500">Total Debt</span><span className="text-gray-900 font-medium">${results.fourYearTotalLoans.toLocaleString()}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Interest Rate</span><span className="text-gray-900 font-medium">6.8% (federal avg)</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Monthly Payment</span><span className="text-gray-900 font-medium">${results.monthlyPayment}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Total Interest</span><span className="text-red-600 font-medium">${results.totalInterest.toLocaleString()}</span></div>
                        <div className="flex justify-between border-t border-gray-200 pt-2 mt-2"><span className="text-gray-800 font-semibold">Total You'll Pay</span><span className="text-gray-900 font-bold">${results.totalRepayment.toLocaleString()}</span></div>
                      </div>
                    </div>
                    <div className="p-5 bg-green-50 border border-green-200 rounded-xl">
                      <h4 className="text-green-700 font-semibold mb-3">How to Pay Off Faster</h4>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5" /> Pay biweekly instead of monthly (1 extra payment/year)</li>
                        <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5" /> Put tax refunds toward principal</li>
                        <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5" /> Round up payments to nearest $50</li>
                        <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5" /> Refinance after building credit (2-3 years)</li>
                      </ul>
                    </div>
                  </div>
                </Card>
              )}

              {/* Disclaimer */}
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">
                    <strong className="text-orange-700">Important:</strong> These are estimates based on BLS, NACE, and Census data. 
                    Your actual costs and earnings will vary. Always verify with official sources and the college's financial aid office.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calculator;
